import { getTruck, setTruck, onTruckChange } from '@theme/dialed-truck';
import { YmmPicker, truckLabel } from '@theme/dialed-ymm';

/**
 * <dialed-truck-picker>: Year → Make → Model → Bed selects that save the shopper's truck
 * (assets/dialed-truck.js) without leaving the page, plus a Clear button. Sections that price
 * by truck (Shop builds) listen with onTruckChange. Truck data: snippets/dialed-truck-data.liquid.
 *
 * Children: select[data-picker="year|make|model|config"], [data-picker-clear], [data-picker-yes]
 * (shown with a truck; its [data-picker-line] gets "2024 Toyota Tacoma · 5 ft bed"),
 * [data-picker-no] (shown without one).
 */
class DialedTruckPicker extends HTMLElement {
  /** @type {YmmPicker | null} */
  picker = null;

  connectedCallback() {
    let fits = [];
    try {
      fits = JSON.parse(this.querySelector('[data-dialed-trucks]')?.textContent ?? '[]');
    } catch {
      return;
    }
    const get = (/** @type {string} */ name) => /** @type {HTMLSelectElement | null} */ (this.querySelector(`[data-picker="${name}"]`));
    const year = get('year');
    const make = get('make');
    const model = get('model');
    const bed = get('config');
    if (!year || !make || !model || !bed) return;
    this.picker = new YmmPicker(fits, { year, make, model, bed });
    this.picker.bind((state) => this.#show(true, !state.year));
    this.querySelector('[data-picker-clear]')?.addEventListener('click', () => {
      this.picker?.set({});
      this.#show(true, true);
    });
    const stored = getTruck();
    this.picker.set(stored ? { year: stored.year, make: stored.make, model: stored.model, fit: stored.fit } : {});
    this.#show(false, false);
    // Follow a truck picked somewhere else on the page (e.g. Your bed layout on the Install page)
    this.#unsubscribe = onTruckChange((truck) => {
      if (!truck || !this.picker) return;
      if (truck.fit === this.picker.fit()?.handle && (!truck.year || truck.year === this.picker.state.year)) return;
      this.picker.set({ year: truck.year, make: truck.make, model: truck.model, fit: truck.fit });
      this.#show(false, false);
    });
  }

  disconnectedCallback() {
    this.#unsubscribe();
  }

  #unsubscribe = () => {};

  /**
   * @param {boolean} save - Save (or clear) the shopper's truck
   * @param {boolean} cleared - The picker was emptied
   */
  #show(save, cleared) {
    if (!this.picker) return;
    const fit = this.picker.fit();
    const { year } = this.picker.state;
    if (save && fit) setTruck({ year, make: fit.make, model: fit.model, fit: fit.handle, label: truckLabel(fit, year) });
    if (save && cleared) setTruck(null);

    const yes = /** @type {HTMLElement | null} */ (this.querySelector('[data-picker-yes]'));
    const no = /** @type {HTMLElement | null} */ (this.querySelector('[data-picker-no]'));
    const clear = /** @type {HTMLElement | null} */ (this.querySelector('[data-picker-clear]'));
    if (yes) yes.hidden = !fit;
    if (no) no.hidden = Boolean(fit);
    if (clear) clear.hidden = !year;
    const line = this.querySelector('[data-picker-line]');
    if (line && fit) line.textContent = truckLabel(fit, year);
  }
}

if (!customElements.get('dialed-truck-picker')) customElements.define('dialed-truck-picker', DialedTruckPicker);
